// src/components/clientgallery.jsx
// Fixes: scan quota display aligned with useSubscription (not old 10-scan limit),
// upgrade prices updated to ₦3,000/₦20,000, no Paystack

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, onSnapshot, doc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useSubscription } from '../hooks/useSubscription';
import SubscriptionPage from './SubscriptionPage';

const GOLD = '#B8860B';

export default function ClientGallery() {
  const { barberId } = useParams();
  const navigate     = useNavigate();
  const sub          = useSubscription();

  const [portfolio,  setPortfolio]  = useState([]);
  const [barberData, setBarberData] = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [showSubModal, setShowSubModal] = useState(false);

  useEffect(() => {
    const unsubBarber = onSnapshot(doc(db, 'barbers', barberId), (snap) => {
      if (snap.exists()) setBarberData(snap.data());
    });

    const unsubPortfolio = onSnapshot(collection(db, 'barbers', barberId, 'portfolio'), (snap) => {
      setPortfolio(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });

    return () => { unsubBarber(); unsubPortfolio(); };
  }, [barberId]);

  const handleTryOn = (styleId) => {
    if (!sub.canStyleTryOn.allowed) { setShowSubModal(true); return; }
    navigate(`/viewer/${styleId}?from=${barberId}`);
  };

  // Quota display
  const isActive  = sub.isSubscriptionActive;
  const scanUsed  = isActive ? sub.monthlyScansUsed   : sub.lifetimeScansUsed;
  const scanCap   = isActive ? (sub.PRICING[sub.tier]?.scansPerMonth || 0) : sub.FREE_LIMITS.scans;
  const quotaText = sub.isSuperAdmin
    ? 'UNLIMITED — ROOT ACCESS'
    : isActive
      ? `${sub.tier?.toUpperCase()} PLAN · ${scanUsed}/${scanCap} SCANS THIS MONTH · UNLIMITED TRY-ONS`
      : `FREE TIER · ${scanUsed}/${scanCap} SCAN USED · ${sub.lifetimeStylesUsed}/${sub.FREE_LIMITS.styleTryOns} TRY-ON USED`;

  if (loading) return <div style={loaderStyle}>LOADING PORTFOLIO...</div>;

  return (
    <div style={container}>
      {showSubModal && (
        <SubscriptionPage
          paywallMode={true}
          paywallReason="style_exhausted"
          onClose={() => setShowSubModal(false)}
          redirectAfter={`/barber/${barberId}`}
        />
      )}

      {/* LIVE STATUS */}
      <div style={statusIndicator}>
        <div style={{
          width: '10px', height: '10px', borderRadius: '50%',
          background: barberData?.status === 'active' ? '#2ecc71' : '#ff4d4d',
          boxShadow: barberData?.status === 'active' ? '0 0 10px #2ecc71' : 'none',
        }} />
        <span style={statusText}>
          {barberData?.status === 'active' ? 'AVAILABLE NOW' : 'FULLY BOOKED'}
        </span>
      </div>

      <h1 style={title}>{barberData?.name?.toUpperCase() || 'BARBER'}'S SHOWCASE</h1>

      {/* SCAN/TRYON QUOTA TRACKER */}
      {!sub.loading && (
        <div style={trackerBox}>
          <p style={trackerText}>{quotaText}</p>
          {!isActive && !sub.isSuperAdmin && (
            <div style={{ marginTop: '10px', display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button style={upgradeBtn} onClick={() => setShowSubModal(true)}>
                MONTHLY — ₦3,000
              </button>
              <button style={{ ...upgradeBtn, background: 'none', border: `1px solid ${GOLD}`, color: GOLD }} onClick={() => setShowSubModal(true)}>
                YEARLY — ₦20,000
              </button>
            </div>
          )}
        </div>
      )}

      {/* PORTFOLIO GRID */}
      <div style={grid}>
        {portfolio.length > 0 ? portfolio.map(item => (
          <div key={item.id} style={card} onClick={() => handleTryOn(item.id)}>
            <img src={item.textureUrl || item.image} style={img} alt={item.styleName} />
            <div style={overlayGrad}>
              <p style={styleName}>{item.styleName}</p>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={tryBtn} onClick={e => { e.stopPropagation(); handleTryOn(item.id); }}>
                  TRY ON {!sub.canStyleTryOn.allowed && '🔒'}
                </button>
                <button style={bookNowBtn} onClick={e => { e.stopPropagation(); navigate(`/book/${barberId}?style=${item.id}`); }}>
                  BOOK
                </button>
              </div>
            </div>
          </div>
        )) : (
          <div style={emptyState}>
            <p>No architectures uploaded to this vault yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const container       = { minHeight:'100vh', background:'#000', padding:'20px', color:'white', fontFamily:'Montserrat, sans-serif' };
const statusIndicator = { display:'flex', alignItems:'center', gap:'8px', marginBottom:'10px', justifyContent:'center' };
const statusText      = { fontSize:'0.65rem', fontWeight:'bold', letterSpacing:'1px', color:'rgba(255,255,255,0.8)' };
const title           = { color:GOLD, textAlign:'center', letterSpacing:'3px', fontSize:'1.2rem', marginBottom:'30px', fontWeight:'900' };
const trackerBox      = { background:'rgba(255,255,255,0.05)', padding:'15px', borderRadius:'20px', textAlign:'center', marginBottom:'30px', border:'1px solid rgba(184,134,11,0.2)' };
const trackerText     = { fontSize:'0.75rem', color:GOLD, fontWeight:'bold', margin:0 };
const upgradeBtn      = { background:GOLD, border:'none', padding:'8px 20px', borderRadius:'50px', fontSize:'0.65rem', fontWeight:'900', color:'black', cursor:'pointer' };
const grid            = { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'15px' };
const card            = { borderRadius:'24px', overflow:'hidden', position:'relative', height:'240px', border:'1px solid rgba(255,255,255,0.1)', cursor:'pointer' };
const img             = { width:'100%', height:'100%', objectFit:'cover' };
const overlayGrad     = { position:'absolute', bottom:0, width:'100%', background:'linear-gradient(transparent,rgba(0,0,0,0.95))', padding:'15px' };
const styleName       = { fontSize:'0.8rem', fontWeight:'bold', marginBottom:'8px', color:'#fff' };
const tryBtn          = { background:'white', color:'black', border:'none', padding:'8px 12px', borderRadius:'50px', fontSize:'0.6rem', fontWeight:'900', cursor:'pointer', flex:1 };
const bookNowBtn      = { background:GOLD, color:'black', border:'none', padding:'8px 12px', borderRadius:'50px', fontSize:'0.6rem', fontWeight:'900', cursor:'pointer', flex:1 };
const emptyState      = { gridColumn:'1/-1', textAlign:'center', opacity:0.4, marginTop:'60px', fontSize:'0.8rem' };
const loaderStyle     = { height:'100vh', background:'#000', color:GOLD, display:'grid', placeItems:'center', fontSize:'0.8rem', fontWeight:'bold', letterSpacing:'2px' };
